import { z } from 'zod';
import { DEVICE_ONLY_PERMISSIONS, MANAGE_IMPLIES, OPERATOR_ONLY_PERMISSIONS, PARTNER_ONLY_PERMISSIONS, PARTNER_SETUP_EXCLUDED_PERMISSIONS, PERMISSIONS, isPermission, permissionAction, } from './permissions.js';
export const SYSTEM_ROLES = {
    'tenant.owner': ['platform.*', 'visitor.*', 'directory.*'], // includes billing
    'tenant.admin': [
        'platform.person.*',
        'platform.location.*',
        'platform.department.*',
        'platform.membership.read',
        'platform.user.manage',
        'platform.import.manage',
        // S37: named literally because `tenant.admin`'s patterns are per-resource — there is no
        // `platform.*` here to sweep it up the way `tenant.owner`'s does. A tenant admin holds
        // every permission the wizard's five steps need, so it must be able to read its progress.
        'platform.onboarding.read',
        // S42-m4dr Phase 4: named literally for the same reason `platform.onboarding.read` is —
        // `tenant.admin` has no `platform.*` wildcard, so a tenant admin must be able to read the
        // tenant's own audit trail explicitly.
        'platform.audit.read',
        // docs/adr/0032 Slice 3: the tenant's own Settings → Privacy (kiosk usage analytics). Named
        // per-resource like the rest of this role; `manage` implies `read` during expansion.
        'platform.settings.*',
        // PRD-010: Settings → Integrations (Slack / Teams). Per-resource like the rest of this role.
        'platform.integrations.*',
        // S49-3u50: external directory sync. Per-resource like the rest of this role.
        'platform.directory-sync.*',
        // S50-kqp9: company sign-in. Per-resource like the rest of this role.
        'platform.sso.*',
        // Handoff 0017 §2: allowed email domains. Per-resource like the rest of this role.
        'platform.email-domain.*',
        // adr/0037 decision 7: every employee can register their own phone for push.
        'platform.push-installation.manage',
        'visitor.*',
        // docs/adr/0036: a tenant admin runs the Employee Directory too.
        'directory.*',
    ],
    'visitor.admin': [
        'visitor.*',
        'platform.person.read',
        'platform.location.read',
        'platform.push-installation.manage',
    ],
    /*
     * `platform.location.read` on every visitor role (manager, frontdesk, viewer): the Visitor
     * SPA's location pickers call `GET /locations` and `GET /locations/app-subscriptions`, and
     * without it these users get a 403 and an empty picker. It is a read, and every route that
     * declares it narrows by `allowedLocationIds(auth, 'platform.location.read')`, so a grant
     * scoped to Building A still sees only Building A. Nothing write-side rides on it.
     */
    'visitor.manager': [
        'visitor.visit.manage',
        'visitor.hosted-visit.read',
        'visitor.hosted-visit.update',
        'visitor.flow.read',
        'platform.person.read',
        'platform.location.read',
        'platform.push-installation.manage',
    ],
    /**
     * The receptionist. Signs visitors in and out, and must be able to *read* the
     * check-in flow configuration to drive the kiosk-equivalent web flow — but
     * never `visitor.flow.manage`: authoring a tenant's screens is an
     * administrative act (`visitor.admin` / `tenant.admin` / `tenant.owner`).
     */
    'visitor.frontdesk': [
        'visitor.visit.read',
        'visitor.visit.create',
        'visitor.hosted-visit.read',
        'visitor.hosted-visit.update',
        'visitor.flow.read',
        'platform.person.read',
        'platform.location.read',
        'platform.push-installation.manage',
    ],
    'visitor.viewer': [
        'visitor.visit.read',
        'visitor.hosted-visit.read',
        'visitor.hosted-visit.update',
        'platform.location.read',
        'platform.push-installation.manage',
    ],
    /** docs/adr/0036: everything in the Employee Directory, nothing outside it. */
    'directory.admin': ['directory.*'],
    /** docs/adr/0036: browse the directory and edit your own profile. Not a seat. */
    'directory.member': ['directory.person.read', 'directory.profile.update'],
    /** adr/0037 decision 3: an employee who hosts visitors and uses InstaCheckin Express; nothing on the admin consoles. */
    'visitor.host': ['visitor.hosted-visit.read', 'visitor.hosted-visit.update', 'platform.push-installation.manage'],
};
/**
 * Non-empty tuple so `z.enum` can consume it. Derived from `SYSTEM_ROLES` rather
 * than restated, so a new role cannot be added without the write-side validator
 * learning about it (docs/005 §Enforcement: "`roleKey` written to the DB is
 * real").
 */
const ROLE_KEY_TUPLE = Object.keys(SYSTEM_ROLES);
export const ROLE_KEYS = ROLE_KEY_TUPLE;
/** adr/0036 decision 2 (generalised 2026-10-17): employee-tier roles give an employee login access to an
 *  app without occupying a seat. Typed Record<RoleKey, …> so a new role must declare its tier. */
export const ROLE_TIERS = {
    'tenant.owner': 'seat',
    'tenant.admin': 'seat',
    'visitor.admin': 'seat',
    'visitor.manager': 'seat',
    'visitor.frontdesk': 'seat',
    'visitor.viewer': 'seat',
    'directory.admin': 'employee',
    'directory.member': 'employee',
    'visitor.host': 'employee',
};
/** Roles tagged `employee` in `ROLE_TIERS`. */
export const EMPLOYEE_TIER_ROLE_KEYS = ROLE_KEYS.filter((k) => ROLE_TIERS[k] === 'employee');
/** adr/0036 decision 2: true iff `roleKeys` is non-empty and every key is employee-tier. */
export function isEmployeeTierRoleSet(roleKeys) {
    return roleKeys.length > 0 && roleKeys.every((k) => EMPLOYEE_TIER_ROLE_KEYS.includes(k));
}
/** Validates a `roleKey` before it is written to `RoleAssignment`. */
export const roleKeySchema = z.enum(ROLE_KEY_TUPLE);
/**
 * Device roles — docs/005 §"Paired devices": one device role per `appId`,
 * defined in code beside the human roles and LOCATION-scoped to the device by
 * the middleware. No per-device permission rows, ever.
 *
 * `mailroom-kiosk` is deliberately **empty**: no `mailroom.*` permission exists
 * yet, and inventing one before the Mailroom app ships would be a permission
 * with no feature behind it. An empty set is the smallest defensible grant and
 * it is also the correct one — deny by default means a paired mailroom device
 * can reach nothing, which is exactly the payoff docs/005 claims ("a paired
 * mailroom device cannot call visitor check-in routes"). It gains permissions
 * when the app that needs them lands.
 */
export const DEVICE_ROLES = {
    /**
     * `visitor.flow.read` is load-bearing, not decoration: a paired kiosk's first
     * call is the effective-flow endpoint that tells it which screens to render.
     * Without this the device gets a 403 and shows nothing. It stays read-only —
     * a device can never author a flow, and LOCATION scoping (applied by the
     * middleware from the `Device` row) confines it to its own location's flow.
     *
     * `visitor.visit.self-check-out` (PRD-004 CHK-16, docs/plans/kiosk-self-check-out.md) is
     * named literally, not reached through any pattern — this is the ONLY place in either role
     * map that may grant it (see `DEVICE_ONLY_PERMISSIONS` in `permissions.ts`). It lets this
     * device close out a visit a visitor re-identifies themselves against at their own
     * location; it is distinct from `visitor.visit.manage`, which this role deliberately does
     * not hold.
     *
     * `visitor.badge.print` (S35 PRT-01b/04/12) is named literally for the same reason: a paired
     * kiosk resolving which printer it prints badges to, and later reporting that it did, is a
     * device-only capability no human role may reach.
     */
    'visitor-kiosk': [
        'visitor.visit.create',
        'visitor.visit.read',
        'visitor.flow.read',
        'visitor.visit.self-check-out',
        'visitor.badge.print',
    ],
    'mailroom-kiosk': [],
};
/**
 * Partner roles — docs/adr/0035, docs/005 "Partner principals". Held by a partner principal,
 * never by a `RoleAssignment`, so they are deliberately NOT part of `ROLE_KEYS` / `roleKeySchema`.
 * Every permission here is in `PARTNER_ONLY_PERMISSIONS`. Nothing consults them yet: the partner
 * branch of `can()` arrives with the portal and setup phases.
 */
export const PARTNER_ROLES = {
    'partner.owner': [
        'platform.partner-customer.read',
        'platform.partner-customer.create',
        'platform.partner-setup.create',
        'platform.partner-team.manage',
        'platform.partner-statement.read',
        'platform.partner-billing.manage',
    ],
    'partner.admin': [
        'platform.partner-customer.read',
        'platform.partner-customer.create',
        'platform.partner-setup.create',
        'platform.partner-team.manage',
    ],
    'partner.agent': ['platform.partner-customer.read', 'platform.partner-setup.create'],
};
const PARTNER_ROLE_KEY_TUPLE = Object.keys(PARTNER_ROLES);
export const PARTNER_ROLE_KEYS = PARTNER_ROLE_KEY_TUPLE;
export const partnerRoleKeySchema = z.enum(PARTNER_ROLE_KEY_TUPLE);
/**
 * What a partner holds inside a customer tenant while setup access is on (docs/adr/0035
 * decision 5): configuration only. Listed literally — `assertRolesAreLive()` rejects a wildcard
 * here — and `PARTNER_SETUP_EXCLUDED_PERMISSIONS` keeps every visitor-data permission out.
 */
const SETUP_PERMISSIONS = [
    'platform.location.manage',
    'platform.department.manage',
    'platform.membership.read',
    'platform.user.manage',
    'platform.onboarding.read',
    'platform.settings.manage',
    'visitor.flow.manage',
    'visitor.device.manage',
    'visitor.printer.manage',
    'visitor.settings.manage',
];
export const PARTNER_SETUP_ROLES = {
    'partner.setup': [...SETUP_PERMISSIONS],
    'partner.setup-wholesale': [...SETUP_PERMISSIONS, 'platform.billing.manage'],
};
export const PARTNER_SETUP_ROLE_KEYS = Object.keys(PARTNER_SETUP_ROLES);
const EMPTY_PERMISSIONS = new Set();
/**
 * Expands one pattern against the real permission list.
 *
 * A pattern is either a literal permission or a dot-segmented glob whose final
 * segment is `*`, matching **one or more** trailing segments — so `platform.*`
 * matches `platform.person.read` and `platform.person.*` matches it too, but
 * neither matches the bare prefix itself.
 *
 * Operator-only permissions are NOT filtered here — `assertRolesAreLive()` needs
 * to see them to diagnose a bad pattern. Filtering happens in `grantsFor`.
 */
function expandPattern(pattern) {
    if (pattern.endsWith('.*')) {
        // Drop only the `*`, keeping the dot, so the prefix cannot match a sibling
        // resource whose name merely starts with the same characters.
        const prefix = pattern.slice(0, -1);
        return PERMISSIONS.filter((p) => p.startsWith(prefix) && p.length > prefix.length);
    }
    return PERMISSIONS.filter((p) => p === pattern);
}
/**
 * `manage` implies `create | read | update | delete` on the *same* resource
 * (docs/005 §Permissions line 13) — and only for actions that actually exist in
 * `PERMISSIONS`, so the implication can never mint a permission no feature
 * backs. The implication is per-resource: `platform.user.manage` does not imply
 * `platform.membership.read`.
 */
function impliedByManage(permission) {
    if (permissionAction(permission) !== 'manage')
        return [];
    const resource = permission.slice(0, permission.lastIndexOf('.'));
    const implied = [];
    for (const action of MANAGE_IMPLIES) {
        const candidate = `${resource}.${action}`;
        if (isPermission(candidate) && !OPERATOR_ONLY_PERMISSIONS.has(candidate)) {
            implied.push(candidate);
        }
    }
    return implied;
}
/**
 * The full grant of a pattern list.
 *
 * **`forbidden` is skipped unconditionally.** Without this, `tenant.owner`'s `platform.*`
 * would expand to include `platform.tenant.read` and every tenant owner would hold a
 * cross-tenant read capability — see `OPERATOR_ONLY_PERMISSIONS` in `permissions.ts`. Human
 * role maps additionally forbid `DEVICE_ONLY_PERMISSIONS` for the symmetric reason stated on
 * that constant: a `visitor.*` wildcard must not sweep up a device-only capability either.
 */
function grantsFor(patterns, forbidden) {
    const granted = new Set();
    for (const pattern of patterns) {
        for (const permission of expandPattern(pattern)) {
            if (forbidden.has(permission))
                continue;
            granted.add(permission);
            for (const implied of impliedByManage(permission))
                granted.add(implied);
        }
    }
    return granted;
}
function expandRoleMap(roles, forbidden) {
    return new Map(Object.entries(roles).map(([key, patterns]) => [key, grantsFor(patterns, forbidden)]));
}
/**
 * The permissions a role map may never hold, by map name. A name outside the live maps throws, so
 * a fabricated or renamed map cannot silently get a weaker set.
 *
 *   SYSTEM_ROLES        → operator-only ∪ device-only ∪ partner-only
 *   DEVICE_ROLES        → operator-only ∪ partner-only
 *   PARTNER_ROLES       → every permission that is NOT partner-only
 *   PARTNER_SETUP_ROLES → operator-only ∪ device-only ∪ partner-only ∪ setup-excluded
 */
function forbiddenForMap(mapName) {
    switch (mapName) {
        case 'SYSTEM_ROLES':
            return new Set([
                ...OPERATOR_ONLY_PERMISSIONS,
                ...DEVICE_ONLY_PERMISSIONS,
                ...PARTNER_ONLY_PERMISSIONS,
            ]);
        case 'DEVICE_ROLES':
            return new Set([...OPERATOR_ONLY_PERMISSIONS, ...PARTNER_ONLY_PERMISSIONS]);
        case 'PARTNER_ROLES':
            return new Set(PERMISSIONS.filter((p) => !PARTNER_ONLY_PERMISSIONS.has(p)));
        case 'PARTNER_SETUP_ROLES':
            return new Set([
                ...OPERATOR_ONLY_PERMISSIONS,
                ...DEVICE_ONLY_PERMISSIONS,
                ...PARTNER_ONLY_PERMISSIONS,
                ...PARTNER_SETUP_EXCLUDED_PERMISSIONS,
            ]);
        default:
            throw new Error(`unknown role map "${mapName}"`);
    }
}
/**
 * Computed once, at module load. Resolution is set math, never a scan.
 *
 * `SYSTEM_ROLE_GRANTS` additionally forbids `DEVICE_ONLY_PERMISSIONS` — no human role may
 * reach one, not even through a wildcard. `DEVICE_ROLE_GRANTS` does not: naming one literally
 * in `DEVICE_ROLES` (see `visitor-kiosk` above) is the only way it is ever granted.
 */
const SYSTEM_ROLE_GRANTS = expandRoleMap(SYSTEM_ROLES, forbiddenForMap('SYSTEM_ROLES'));
const DEVICE_ROLE_GRANTS = expandRoleMap(DEVICE_ROLES, forbiddenForMap('DEVICE_ROLES'));
const PARTNER_ROLE_GRANTS = expandRoleMap(PARTNER_ROLES, forbiddenForMap('PARTNER_ROLES'));
const PARTNER_SETUP_ROLE_GRANTS = expandRoleMap(PARTNER_SETUP_ROLES, forbiddenForMap('PARTNER_SETUP_ROLES'));
/**
 * **Fail closed.** An unknown `roleKey` — a role removed from code while rows
 * still reference it — grants nothing and does not throw (docs/005 §"Scopes and
 * assignments"). authz owns no logger; `apps/api` logs the orphan when it builds
 * the `AuthContext`.
 */
export function permissionsForRole(roleKey) {
    return SYSTEM_ROLE_GRANTS.get(roleKey) ?? EMPTY_PERMISSIONS;
}
/** Same fail-closed contract as `permissionsForRole`, keyed by `appId`. */
export function permissionsForDeviceRole(appId) {
    return DEVICE_ROLE_GRANTS.get(appId) ?? EMPTY_PERMISSIONS;
}
/**
 * Same fail-closed contract, keyed by a partner role key. Covers both `PARTNER_ROLES` and
 * `PARTNER_SETUP_ROLES`; a `RoleAssignment` key such as `tenant.owner` yields nothing. Nothing
 * calls this yet — the partner branch of `can()` arrives in the portal and setup phases.
 */
export function permissionsForPartnerRole(roleKey) {
    return PARTNER_ROLE_GRANTS.get(roleKey) ?? PARTNER_SETUP_ROLE_GRANTS.get(roleKey) ?? EMPTY_PERMISSIONS;
}
/** The maps `assertRolesAreLive()` checks when called with no argument. */
const LIVE_ROLE_MAPS = {
    SYSTEM_ROLES,
    DEVICE_ROLES,
    PARTNER_ROLES,
    PARTNER_SETUP_ROLES,
};
const FORBIDDEN_REASON = {
    'operator-only': 'Platform-operator capability is not tenant-grantable (docs/005 §Platform operators) ' +
        '— it comes from User.isPlatformOperator, never from a role.',
    'device-only': 'It exists only for a paired device principal (docs/005 §"Paired devices") and is ' +
        'never tenant-grantable to a human role (see DEVICE_ONLY_PERMISSIONS in permissions.ts).',
    'partner-only': 'It exists only for a partner principal (docs/adr/0035) and is held by PARTNER_ROLES alone ' +
        '(see PARTNER_ONLY_PERMISSIONS in permissions.ts).',
    'setup-excluded': 'A partner setup role never holds customer data (docs/adr/0035 decision 5; see ' +
        'PARTNER_SETUP_EXCLUDED_PERMISSIONS in permissions.ts).',
    'not-partner-only': 'A partner role holds partner-only permissions alone (docs/adr/0035).',
};
function forbiddenKind(permission) {
    if (OPERATOR_ONLY_PERMISSIONS.has(permission))
        return 'operator-only';
    if (DEVICE_ONLY_PERMISSIONS.has(permission))
        return 'device-only';
    if (PARTNER_ONLY_PERMISSIONS.has(permission))
        return 'partner-only';
    if (PARTNER_SETUP_EXCLUDED_PERMISSIONS.has(permission))
        return 'setup-excluded';
    return 'not-partner-only';
}
/**
 * Startup assertion — docs/005 §Enforcement, "Role patterns are live".
 *
 * Throws when:
 *   (a) a pattern grants nothing, because it matches no real permission at all
 *       or matches only operator-only ones;
 *   (b) a pattern names an operator-only permission literally, which is the one
 *       way a role could otherwise reach cross-tenant capability.
 *
 * Called once from `index.ts`, so a bad map kills the process at import rather
 * than silently granting nothing in production. The parameter exists so tests
 * can assert on fabricated maps; production always calls it with no argument.
 */
export function assertRolesAreLive(roleMaps = LIVE_ROLE_MAPS) {
    for (const [mapName, roles] of Object.entries(roleMaps)) {
        // Each live map has its own forbidden set; an unknown map name throws.
        const forbidden = forbiddenForMap(mapName);
        for (const [roleKey, patterns] of Object.entries(roles)) {
            for (const pattern of patterns) {
                if (mapName === 'PARTNER_SETUP_ROLES' && pattern.endsWith('.*')) {
                    throw new Error(`${mapName}["${roleKey}"] declares the wildcard "${pattern}"; setup roles list permissions literally (adr/0035).`);
                }
                if (isPermission(pattern) && forbidden.has(pattern)) {
                    const kind = forbiddenKind(pattern);
                    throw new Error(`${mapName}["${roleKey}"] names the ${kind} permission "${pattern}". ${FORBIDDEN_REASON[kind]}`);
                }
                const matched = expandPattern(pattern);
                const effective = matched.filter((p) => !forbidden.has(p));
                if (effective.length === 0) {
                    throw new Error(`${mapName}["${roleKey}"] declares the pattern "${pattern}", which grants no ` +
                        'permission. Dead or misspelled patterns fail the process rather than silently ' +
                        'granting nothing (docs/005 §Roles).');
                }
            }
            // Defensive: unreachable while `grantsFor` skips `forbidden`, and it must stay
            // unreachable. This is the tripwire if that ever changes.
            for (const permission of grantsFor(patterns, forbidden)) {
                if (forbidden.has(permission)) {
                    throw new Error(`${mapName}["${roleKey}"] expands to the forbidden permission "${permission}".`);
                }
            }
        }
    }
}
//# sourceMappingURL=roles.js.map